import React from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import ScrollFadeIn from '../components/ScrollFadeIn';
import {
  GraduationCap,
  UserCheck,
  Users,
  Book,
  Target,
  MessageSquare,
  BarChart3,
  Gem,
  Banknote,
  Handshake,
  LineChart,
  Calculator,
  FlaskConical,
  Globe,
  Laptop,
} from 'lucide-react';

const KAIStudyPage: React.FC = () => {
  return (
    <PageContainer>
      <HeroSection>
        <HeroContent>
          {/* 아이콘 교체 */}
          <HeroIcon><GraduationCap /></HeroIcon>
          <HeroTitle>KAIStudy</HeroTitle>
          <HeroTagline>KAIST 학생들의 프리미엄 그룹과외</HeroTagline>
          <HeroDescription>
            KAIST 재학생들이 직접 진행하는 고품질 그룹 과외 플랫폼.
            최고 수준의 강사진과 함께 효율적인 학습을 경험하세요.
          </HeroDescription>
        </HeroContent>
      </HeroSection>

      <FeaturesSection>
        <SectionTitle>KAIStudy만의 특별함</SectionTitle>
        <FeaturesGrid>
          <ScrollFadeIn direction="up" delay={0.1}>
            <FeatureCard>
              {/* 아이콘 교체 */}
              <FeatureIcon><UserCheck /></FeatureIcon>
              <FeatureTitle>검증된 KAIST 학생 강사</FeatureTitle>
              <FeatureDescription>
                대한민국 최고 수준의 과학기술 대학인 KAIST의 재학생들이
                직접 강의합니다. 학생 신분을 철저히 검증하여 질 높은
                교육을 보장합니다.
              </FeatureDescription>
            </FeatureCard>
          </ScrollFadeIn>

          <ScrollFadeIn direction="up" delay={0.2}>
            <FeatureCard>
              {/* 아이콘 교체 */}
              <FeatureIcon><Users /></FeatureIcon>
              <FeatureTitle>소규모 그룹 과외</FeatureTitle>
              <FeatureDescription>
                3-5명의 소규모 그룹으로 운영되어 개인별 맞춤 지도가
                가능합니다.
                그룹 학습의 장점과 개인 과외의 장점을
                모두 누릴 수 있습니다.
              </FeatureDescription>
            </FeatureCard>
          </ScrollFadeIn>

          <ScrollFadeIn direction="up" delay={0.3}>
            <FeatureCard>
              {/* 아이콘 교체 */}
              <FeatureIcon><Book /></FeatureIcon>
              <FeatureTitle>다양한 과목 선택</FeatureTitle>
              <FeatureDescription>
                수학, 과학, 영어, 프로그래밍 등 다양한 과목을 제공합니다.
                각 과목별로 전문성을 갖춘 강사진이 배정되어 체계적인
                학습이 가능합니다.
              </FeatureDescription>
            </FeatureCard>
          </ScrollFadeIn>

          <ScrollFadeIn direction="up" delay={0.4}>
            <FeatureCard>
              {/* 아이콘 교체 */}
              <FeatureIcon><Target /></FeatureIcon>
              <FeatureTitle>맞춤형 커리큘럼</FeatureTitle>
              <FeatureDescription>
                학생들의 수준과 목표에 맞춘 커스터마이징된 커리큘럼을
                제공합니다.
                수시, 정시, 내신 등 다양한 목적에 맞춘
                학습이 가능합니다.
              </FeatureDescription>
            </FeatureCard>
          </ScrollFadeIn>

          <ScrollFadeIn direction="up" delay={0.5}>
            <FeatureCard>
              {/* 아이콘 교체 */}
              <FeatureIcon><MessageSquare /></FeatureIcon>
              <FeatureTitle>실시간 채팅 상담</FeatureTitle>
              <FeatureDescription>
                강사와 언제든지 소통할 수 있는 채팅 시스템을 제공합니다.
                수업 외 시간에도 궁금한 점을 질문하고 답변받을 수
                있습니다.
              </FeatureDescription>
            </FeatureCard>
          </ScrollFadeIn>

          <ScrollFadeIn direction="up" delay={0.6}>
            <FeatureCard>
              {/* 아이콘 교체 */}
              <FeatureIcon><BarChart3 /></FeatureIcon>
              <FeatureTitle>학습 진도 관리</FeatureTitle>
              <FeatureDescription>
                체계적인 진도 관리와 성적 추적으로 학습 효과를
                극대화합니다.
                정기적인 피드백으로 약점을 보완하고
                강점을 더욱 발전시킬 수 있습니다.
              </FeatureDescription>
            </FeatureCard>
          </ScrollFadeIn>
        </FeaturesGrid>
      </FeaturesSection>

      <ProcessSection>
        <SectionTitle>이용 방법</SectionTitle>
        <ProcessGrid>
          <ScrollFadeIn direction="right" delay={0.1}>
            <ProcessStep>
              <StepNumber>1</StepNumber>
              <StepContent>
                <StepTitle>강사 검색</StepTitle>
                <StepDescription>
                  원하는 과목, 학년, 요일 등의 조건으로
                  자신에게 맞는 강사를 찾아보세요
                </StepDescription>
              </StepContent>
            </ProcessStep>
          </ScrollFadeIn>

          <ScrollFadeIn direction="right" delay={0.2}>
            <ProcessStep>
              <StepNumber>2</StepNumber>
              <StepContent>
                <StepTitle>과외 신청</StepTitle>
                <StepDescription>
                  강사의 프로필과 후기를 확인하고
                  과외를 신청하세요
                </StepDescription>
              </StepContent>
            </ProcessStep>
          </ScrollFadeIn>

          <ScrollFadeIn direction="right" delay={0.3}>
            <ProcessStep>
              <StepNumber>3</StepNumber>
              <StepContent>
                <StepTitle>매칭 확정</StepTitle>
                <StepDescription>
                  강사와 상담 후 일정과 커리큘럼을
                  조율하여 매칭을 확정합니다
                </StepDescription>
              </StepContent>
            </ProcessStep>
          </ScrollFadeIn>

          <ScrollFadeIn direction="right" delay={0.4}>
            <ProcessStep>
              <StepNumber>4</StepNumber>
              <StepContent>
                <StepTitle>수업 시작</StepTitle>
                <StepDescription>
                  정해진 일정에 맞춰 수업을 시작하고
                  실시간 채팅으로 소통하세요
                </StepDescription>
              </StepContent>
            </ProcessStep>
          </ScrollFadeIn>
        </ProcessGrid>
      </ProcessSection>

      <BenefitsSection>
        <BenefitsContent>
          <BenefitsLeft>
            <SectionTitle style={{ textAlign: 'left' }}>
              왜 KAIStudy인가?
            </SectionTitle>
            <BenefitsList>
              <BenefitItem>
                {/* 아이콘 교체 */}
                <BenefitIcon><Gem /></BenefitIcon>
                <BenefitText>
                  <BenefitTitle>프리미엄 교육 품질</BenefitTitle>
                  <BenefitDescription>
                    대한민국 최고 수준의 KAIST 학생들이 직접 가르치는
                    프리미엄 교육을 경험하세요.
                  </BenefitDescription>
                </BenefitText>
              </BenefitItem>

              <BenefitItem>
                {/* 아이콘 교체 */}
                <BenefitIcon><Banknote /></BenefitIcon>
                <BenefitText>
                  <BenefitTitle>합리적인 가격</BenefitTitle>
                  <BenefitDescription>
                    그룹 과외 형식으로 개인 과외 대비 저렴한 가격에
                    고품질 교육을 받을 수 있습니다.
                  </BenefitDescription>
                </BenefitText>
              </BenefitItem>

              <BenefitItem>
                {/* 아이콘 교체 */}
                <BenefitIcon><Handshake /></BenefitIcon>
                <BenefitText>
                  <BenefitTitle>유연한 학습 환경</BenefitTitle>
                  <BenefitDescription>
                    온라인/오프라인 선택 가능하며, 일정 조율이
                    유연하여 학생의 편의를 최우선으로 합니다.
                  </BenefitDescription>
                </BenefitText>
              </BenefitItem>

              <BenefitItem>
                {/* 아이콘 교체 */}
                <BenefitIcon><LineChart /></BenefitIcon>
                <BenefitText>
                  <BenefitTitle>입증된 학습 효과</BenefitTitle>
                  <BenefitDescription>
                    체계적인 커리큘럼과 개인별 맞춤 지도로
                    확실한 성적 향상을 경험할 수 있습니다.
                  </BenefitDescription>
                </BenefitText>
              </BenefitItem>
            </BenefitsList>
          </BenefitsLeft>
        </BenefitsContent>
      </BenefitsSection>

      <SubjectsSection>
        <SectionTitle>제공 과목</SectionTitle>
        <SubjectsGrid>
          <SubjectCard>
            <SubjectIcon><Calculator /></SubjectIcon>
            <SubjectTitle>수학</SubjectTitle>
            <SubjectDescription>
              미적분, 확률과 통계, 기하 등 모든 수학 과목
            </SubjectDescription>
          </SubjectCard>

          <SubjectCard>
            <SubjectIcon><FlaskConical /></SubjectIcon>
            <SubjectTitle>과학</SubjectTitle>
            <SubjectDescription>
              물리, 화학, 생명과학, 지구과학
            </SubjectDescription>
          </SubjectCard>

          <SubjectCard>
            <SubjectIcon><Globe /></SubjectIcon>
            <SubjectTitle>영어</SubjectTitle>
            <SubjectDescription>
              문법, 독해, 영작문, 회화
            </SubjectDescription>
          </SubjectCard>

          <SubjectCard>
            <SubjectIcon><Laptop /></SubjectIcon>
            <SubjectTitle>프로그래밍</SubjectTitle>
            <SubjectDescription>
              Python, Java, C++, 알고리즘
            </SubjectDescription>
          </SubjectCard>
        </SubjectsGrid>
      </SubjectsSection>



      <CTASection>
        <CTAContent>
          <CTATitle>KAIStudy와 함께 시작하세요</CTATitle>
          <CTADescription>
            KAIST 학생들과 함께하는 프리미엄 그룹 과외로
            목표를 달성하세요
          </CTADescription>
          <CTAButtons>
            <PrimaryButton to="/contact">
              과외 신청하기
            </PrimaryButton>
            <SecondaryButton to="/contact">
              강사 등록하기
            </SecondaryButton>
          </CTAButtons>
        </CTAContent>
      </CTASection>
    </PageContainer>
  );
};

export default KAIStudyPage;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const PageContainer = styled.div`
  padding-top: 80px;
`;

const HeroSection = styled.section`
  padding: 4rem 2rem;
  background: transparent;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  text-align: center;
  color: ${({ theme }) => theme.colors.textPrimary};
`;
const HeroContent = styled.div`
  max-width: 900px;
  margin: 0 auto;
  animation: ${fadeIn} 1s ease-out;
`;
const HeroIcon = styled.div`
  font-size: 3.25rem;
  margin-bottom: 1.5rem;
  color: ${({ theme }) => theme.colors.accent};
`;

const HeroTitle = styled.h1`
  font-size: 2.6rem;
  font-weight: 800;
  margin-bottom: 1rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 2.1rem;
  }
`;
const HeroTagline = styled.p`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 2rem;
  opacity: 0.95;
`;
const HeroDescription = styled.p`
  font-size: 1.25rem;
  line-height: 1.8;
  opacity: 0.9;
  max-width: 700px;
  margin: 0 auto;
`;
const FeaturesSection = styled.section`
  padding: 4rem 2rem;
  background: ${({ theme }) => theme.colors.bgPrimary};
`;

const SectionTitle = styled.h2`
  font-size: 2.1rem;
  font-weight: 800;
  text-align: center;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 2.5rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 1.9rem;
  }
`;
const FeaturesGrid = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(350px, 100%), 1fr));
  gap: 2rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;
const FeatureCard = styled.div`
  padding: 1.75rem;
  background: ${({ theme }) => theme.colors.bgSecondary};
  backdrop-filter: blur(8px);
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: ${({ theme }) => theme.transitions.cubic};

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.4);
    border-color: rgba(255, 255, 255, 0.2);
  }
`;
const FeatureIcon = styled.div`
  font-size: 2.1rem;
  margin-bottom: 1.5rem;
  color: ${({ theme }) => theme.colors.accent};
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 1rem;
`;

const FeatureDescription = styled.p`
  font-size: 1rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ProcessSection = styled.section`
  padding: 4rem 2rem;
  background: ${({ theme }) => theme.colors.bgSecondary};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const ProcessGrid = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(250px, 100%), 1fr));
  gap: 2rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;
const ProcessStep = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;
const StepNumber = styled.div`
  width: 60px;
  height: 60px;
  background: ${({ theme }) => theme.colors.bgPrimary};
  color: ${({ theme }) => theme.colors.textPrimary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  box-shadow: ${({ theme }) => theme.shadows.small};
`;
const StepContent = styled.div``;

const StepTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 1rem;
`;

const StepDescription = styled.p`
  font-size: 1rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
`;
const BenefitsSection = styled.section`
  padding: 4rem 2rem;
  background: ${({ theme }) => theme.colors.bgPrimary};
`;

const BenefitsContent = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const BenefitsLeft = styled.div``;

const BenefitsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.75rem;
`;
const BenefitItem = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
`;

const BenefitIcon = styled.div`
  font-size: 1.9rem;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.accent};
  margin-top: 0.25rem;
`;

const BenefitText = styled.div`
  flex: 1;
`;

const BenefitTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 0.5rem;
`;

const BenefitDescription = styled.p`
  font-size: 1rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const SubjectsSection = styled.section`
  padding: 4rem 2rem;
  background: transparent;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const SubjectsGrid = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(280px, 100%), 1fr));
  gap: 1.75rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;
const SubjectCard = styled.div`
  padding: 1.75rem;
  background: ${({ theme }) => theme.colors.bgPrimary};
  border-radius: 14px;
  text-align: center;
  transition: ${({ theme }) => theme.transitions.cubic};
  border: 1px solid ${({ theme }) => theme.colors.border};

  &:hover {
    transform: translateY(-8px);
    box-shadow: ${({ theme }) => theme.shadows.medium};
    border-color: rgba(255, 255, 255, 0.2);
  }
`;

const SubjectIcon = styled.div`
  font-size: 2.1rem;
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.colors.accent};
`;
const SubjectTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 0.75rem;
`;
const SubjectDescription = styled.p`
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.6;
`;

const CTASection = styled.section`
  padding: 4rem 2rem;
  background: ${({ theme }) => theme.colors.bgSecondary};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;
const CTAContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  color: ${({ theme }) => theme.colors.textPrimary};
`;
const CTATitle = styled.h2`
  font-size: 2.1rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 1.9rem;
  }
`;
const CTADescription = styled.p`
  font-size: 1.5rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 2rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 1.25rem;
  }
`;
const CTAButtons = styled.div`
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  flex-wrap: wrap;
`;
const PrimaryButton = styled(Link)`
  display: inline-block;
  padding: 0.9rem 2rem;
  background: #ffffff;
  color: #020617;
  border-radius: 8px;
  font-weight: 700;
  font-size: 1.2rem;
  border: none;
  transition: ${({ theme }) => theme.transitions.cubic};
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.3);
  }
`;
const SecondaryButton = styled(Link)`
  display: inline-block;
  padding: 0.9rem 2rem;
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  border: none;
  border-radius: 8px;
  font-weight: 700;
  font-size: 1.2rem;
  backdrop-filter: blur(10px);
  transition: ${({ theme }) => theme.transitions.cubic};
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateY(-4px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  }
`;